import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Link, useLocation } from 'react-router-dom';
import { format, isPast, isToday, isTomorrow, addDays, startOfDay, parseISO } from 'date-fns';
import { ClickToCall } from '@/components/ClickToCall';

// Task type configurations
const TASK_TYPES = {
  call: { icon: '📞', label: 'Call', color: 'bg-blue-100 text-blue-700', border: 'border-blue-300' },
  email: { icon: '📧', label: 'Email', color: 'bg-purple-100 text-purple-700', border: 'border-purple-300' },
  meeting: { icon: '📅', label: 'Meeting', color: 'bg-teal-100 text-teal-700', border: 'border-teal-300' },
  follow_up: { icon: '🔄', label: 'Follow-up', color: 'bg-amber-100 text-amber-700', border: 'border-amber-300' },
  other: { icon: '📌', label: 'Task', color: 'bg-slate-100 text-slate-700', border: 'border-slate-300' },
};

const PRIORITY_CONFIG = {
  urgent: { label: 'Urgent', color: 'bg-red-500 text-white', dot: 'bg-red-500' },
  high: { label: 'High', color: 'bg-orange-100 text-orange-700', dot: 'bg-orange-500' },
  medium: { label: 'Medium', color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  low: { label: 'Low', color: 'bg-slate-100 text-slate-600', dot: 'bg-slate-400' },
};

const STATUS_CONFIG = {
  not_started: { label: 'To Do', color: 'bg-slate-100 text-slate-600' },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700' },
  done: { label: 'Done', color: 'bg-green-100 text-green-700' },
};

// Smart filter tabs
const FILTER_TABS = [
  { id: 'today', label: 'Today', icon: '📍' },
  { id: 'overdue', label: 'Overdue', icon: '🚨' },
  { id: 'upcoming', label: 'Upcoming', icon: '📆' },
  { id: 'all', label: 'All Tasks', icon: '📋' },
  { id: 'completed', label: 'Completed', icon: '✅' },
];

function TaskCenter() {
  const location = useLocation();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('today');
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [leads, setLeads] = useState([]);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Update filter if passed via navigation state
  useEffect(() => {
    if (location.state?.filter) {
      setActiveFilter(location.state.filter);
    }
  }, [location.state?.filter]);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    task_type: 'follow_up',
    priority: 'medium',
    due_date: '',
    due_time: '09:00',
    lead_id: '',
  });

  const fetchTasks = useCallback(async () => {
    try {
      // Cap fetched tasks at 200 — covers virtually all "active" lists for any
      // single user (older tasks pull via the backend pagination cursor).
      // Was 1000; reduced for perf as data scales.
      const response = await axios.get(`${API}/tasks?limit=200`);
      // Backend returns a plain list when no page is provided, or {data, pagination} when paginated.
      const payload = response.data;
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : [];
      setTasks(list);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      setTasks([]);
    }
  }, []);

  const fetchLeads = useCallback(async () => {
    try {
      // Lead lookup map: 50 most recent is plenty for the dropdown — full
      // search uses the AsyncSearchSelect typeahead instead.
      const response = await axios.get(`${API}/leads?limit=50`);
      const data = response.data;
      setLeads(Array.isArray(data) ? data : (data?.data || []));
    } catch (error) {
      console.error('Error fetching leads:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchTasks(), fetchLeads()]);
      setLoading(false);
    };
    loadData();
  }, [fetchTasks, fetchLeads, location.state?.refresh]);

  // Create a lead lookup map for performance
  const leadMap = useMemo(() => {
    const map = new Map();
    leads.forEach(lead => map.set(lead.id, lead));
    return map;
  }, [leads]);

  // Get task's time category using date-string comparison for timezone safety
  const getTaskCategory = useCallback((task) => {
    if (task.status === 'done') return 'completed';
    if (!task.due_date) return 'upcoming';
    
    // Extract the YYYY-MM-DD date portion from the stored due_date.
    // This is the date the user originally picked, regardless of timezone offset.
    const dueDateStr = task.due_date.substring(0, 10);
    
    // Get today's date in the user's local timezone as YYYY-MM-DD
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    
    if (dueDateStr < todayStr) return 'overdue';
    if (dueDateStr === todayStr) return 'today';
    return 'upcoming';
  }, []);

  // Filter tasks based on active filters
  const filteredTasks = useMemo(() => {
    let result = tasks;

    // Filter by time category
    if (activeFilter !== 'all') {
      result = result.filter(task => getTaskCategory(task) === activeFilter);
    }

    // Filter by type
    if (typeFilter !== 'all') {
      result = result.filter(task => task.task_type === typeFilter);
    }

    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(task => 
        task.title?.toLowerCase().includes(query) ||
        task.lead_name?.toLowerCase().includes(query) ||
        task.description?.toLowerCase().includes(query)
      );
    }

    // Sort: overdue first, then by due date
    return result.sort((a, b) => {
      const catA = getTaskCategory(a);
      const catB = getTaskCategory(b);
      if (catA === 'overdue' && catB !== 'overdue') return -1;
      if (catB === 'overdue' && catA !== 'overdue') return 1;
      return new Date(a.due_date || 0) - new Date(b.due_date || 0);
    });
  }, [tasks, activeFilter, typeFilter, searchQuery, getTaskCategory]);

  // Count tasks by category
  const taskCounts = useMemo(() => {
    const counts = { today: 0, overdue: 0, upcoming: 0, all: 0, completed: 0 };
    tasks.forEach(task => {
      const cat = getTaskCategory(task);
      counts[cat]++;
      if (cat !== 'completed') counts.all++;
    });
    counts.all = tasks.filter(t => t.status !== 'done').length;
    return counts;
  }, [tasks, getTaskCategory]);

  // Handle task completion toggle
  const toggleComplete = async (task) => {
    try {
      const newStatus = task.status === 'done' ? 'not_started' : 'done';
      await axios.put(`${API}/tasks/${task.id}`, { status: newStatus });
      fetchTasks();
      setMessage({ 
        type: 'success', 
        text: newStatus === 'done' ? 'Task completed!' : 'Task reopened' 
      });
      setTimeout(() => setMessage({ type: '', text: '' }), 2000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update task' });
    }
  };

  // Handle task creation
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title) {
      setMessage({ type: 'error', text: 'Please enter a task title' });
      return;
    }

    try {
      const taskData = {
        ...formData,
        due_date: formData.due_date ? (() => {
          // Include timezone offset so backend stores the correct UTC equivalent
          const offset = new Date().getTimezoneOffset();
          const sign = offset <= 0 ? '+' : '-';
          const absOffset = Math.abs(offset);
          const tzH = String(Math.floor(absOffset / 60)).padStart(2, '0');
          const tzM = String(absOffset % 60).padStart(2, '0');
          return `${formData.due_date}T${formData.due_time}:00${sign}${tzH}:${tzM}`;
        })() : null,
      };
      delete taskData.due_time;

      await axios.post(`${API}/tasks`, taskData);
      setMessage({ type: 'success', text: 'Task created!' });
      setShowAddModal(false);
      setFormData({
        title: '',
        description: '',
        task_type: 'follow_up',
        priority: 'medium',
        due_date: '',
        due_time: '09:00',
        lead_id: '',
      });
      fetchTasks();
      setTimeout(() => setMessage({ type: '', text: '' }), 2000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to create task' });
    }
  };

  // Delete task
  const deleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    try {
      await axios.delete(`${API}/tasks/${taskId}`);
      fetchTasks();
      setSelectedTask(null);
      setMessage({ type: 'success', text: 'Task deleted' });
      setTimeout(() => setMessage({ type: '', text: '' }), 2000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete task' });
    }
  };

  // Format due date display
  const formatDueDate = (dateStr) => {
    if (!dateStr) return 'No due date';
    // Extract date portion for reliable day comparison
    const dueDatePart = dateStr.substring(0, 10);
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const tmrw = new Date(now);
    tmrw.setDate(tmrw.getDate() + 1);
    const tomorrowStr = `${tmrw.getFullYear()}-${String(tmrw.getMonth() + 1).padStart(2, '0')}-${String(tmrw.getDate()).padStart(2, '0')}`;
    
    const date = parseISO(dateStr);
    if (dueDatePart === todayStr) return `Today at ${format(date, 'h:mm a')}`;
    if (dueDatePart === tomorrowStr) return `Tomorrow at ${format(date, 'h:mm a')}`;
    if (dueDatePart < todayStr) return `Overdue: ${format(date, 'MMM d')}`;
    return format(date, 'MMM d, h:mm a');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-slate-200 border-t-slate-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 -m-4 lg:-m-6 p-4 lg:p-6 space-y-5" data-testid="task-center">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 -mx-4 lg:-mx-6 -mt-4 lg:-mt-6 p-4 lg:p-6 mb-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Task Center</h1>
            <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors"
            data-testid="add-task-btn"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Task
          </button>
        </div>

        {/* Stats Cards - Clean KPI Style */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4 hover:shadow-sm transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Due Today</span>
              <span className="text-lg">📍</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-slate-900 dark:text-slate-100">{taskCounts.today}</span>
              <span className="inline-flex items-center text-xs font-medium text-green-600 dark:text-green-400">
                <svg className="w-3 h-3 mr-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
                0
              </span>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">tasks</p>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-red-100 dark:border-red-900/50 p-4 hover:shadow-sm transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-red-500 dark:text-red-400 uppercase tracking-wide">Overdue</span>
              <span className="text-lg">🚨</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-slate-900 dark:text-slate-100">{taskCounts.overdue}</span>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">tasks</p>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4 hover:shadow-sm transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Upcoming</span>
              <span className="text-lg">📆</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-slate-900 dark:text-slate-100">{taskCounts.upcoming}</span>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">tasks</p>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-green-100 dark:border-green-900/50 p-4 hover:shadow-sm transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-green-600 dark:text-green-400 uppercase tracking-wide">Completed</span>
              <span className="text-lg">✅</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-slate-900 dark:text-slate-100">{taskCounts.completed}</span>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">tasks</p>
          </div>
        </div>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`p-3 rounded-lg ${message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          {message.text}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
        {/* Filter Tabs */}
        <div className="flex items-center gap-1 p-2 border-b border-slate-100 dark:border-slate-700 overflow-x-auto">
          {FILTER_TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveFilter(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                activeFilter === tab.id
                  ? 'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
              data-testid={`filter-${tab.id}`}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
              {tab.id !== 'all' && (
                <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs ${
                  activeFilter === tab.id ? 'bg-slate-700 dark:bg-slate-300' : 'bg-slate-200 dark:bg-slate-600'
                }`}>
                  {taskCounts[tab.id]}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Search and Type Filter */}
        <div className="flex flex-col sm:flex-row gap-3 p-4 border-b border-slate-100 dark:border-slate-700">
          <div className="flex-1 relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search tasks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-1 focus:ring-slate-400 bg-white dark:bg-slate-900 dark:text-slate-100"
              data-testid="search-input"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            <button
              onClick={() => setTypeFilter('all')}
              className={`px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                typeFilter === 'all' ? 'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900' : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
              }`}
            >
              All Types
            </button>
            {Object.entries(TASK_TYPES).map(([key, config]) => (
              <button
                key={key}
                onClick={() => setTypeFilter(key)}
                className={`px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap flex items-center gap-1 ${
                  typeFilter === key ? config.color : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                }`}
              >
                <span>{config.icon}</span>
                <span>{config.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Task List */}
        <div className="divide-y divide-slate-100 dark:divide-slate-700">
          {filteredTasks.length === 0 ? (
            <div className="p-12 text-center">
              <div className="text-4xl mb-3">📋</div>
              <p className="text-slate-500 dark:text-slate-400">No tasks found</p>
              <button
                onClick={() => setShowAddModal(true)}
                className="mt-3 text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-medium"
              >
                + Create your first task
              </button>
            </div>
          ) : (
            filteredTasks.map(task => {
              const typeConfig = TASK_TYPES[task.task_type] || TASK_TYPES.other;
              const priorityConfig = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
              const isOverdue = getTaskCategory(task) === 'overdue';
              const isCompleted = task.status === 'done';
              const lead = leadMap.get(task.lead_id);

              return (
                <div
                  key={task.id}
                  className={`p-4 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors ${isOverdue ? 'bg-red-50/50 dark:bg-red-900/20' : ''} ${isCompleted ? 'opacity-60' : ''}`}
                  data-testid={`task-item-${task.id}`}
                >
                  <div className="flex items-start gap-4">
                    {/* Checkbox */}
                    <button
                      onClick={() => toggleComplete(task)}
                      className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                        isCompleted 
                          ? 'bg-green-500 border-green-500 text-white' 
                          : 'border-slate-300 dark:border-slate-600 hover:border-indigo-500'
                      }`}
                      data-testid={`toggle-complete-${task.id}`}
                    >
                      {isCompleted && <span className="text-xs">✓</span>}
                    </button>

                    {/* Task Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">{typeConfig.icon}</span>
                        <h3 className={`font-medium text-slate-900 dark:text-slate-100 ${isCompleted ? 'line-through' : ''}`}>
                          {task.title}
                        </h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${priorityConfig.color}`}>
                          {priorityConfig.label}
                        </span>
                        {task.auto_generated && (
                          <span
                            className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-200 dark:border-amber-800 inline-flex items-center gap-1"
                            title={task.rule_id ? `Auto-generated by rule: ${task.rule_id}` : 'Auto-generated follow-up'}
                            data-testid={`task-auto-chip-${task.id}`}
                          >
                            <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            Auto
                          </span>
                        )}
                      </div>

                      {task.description && (
                        <p className="text-sm text-slate-500 dark:text-slate-400 mb-2 line-clamp-1">{task.description}</p>
                      )}

                      <div className="flex flex-wrap items-center gap-3 text-sm">
                        {/* Due Date */}
                        <span className={`flex items-center gap-1 ${isOverdue ? 'text-red-600 dark:text-red-400 font-medium' : 'text-slate-500 dark:text-slate-400'}`}>
                          <span>🗓</span>
                          {formatDueDate(task.due_date)}
                        </span>

                        {/* Lead */}
                        {task.lead_name && (
                          <Link 
                            to={`/leads/${task.lead_id}`}
                            className="flex items-center gap-1 text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300"
                          >
                            <span>👤</span>
                            {task.lead_name}
                          </Link>
                        )}

                        {/* Assigned */}
                        {task.assigned_to_name && (
                          <span className="flex items-center gap-1 text-slate-500 dark:text-slate-400">
                            <span>📎</span>
                            {task.assigned_to_name}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {/* Click to Call if lead has phone */}
                      {lead?.phone && (
                        <ClickToCall
                          phone={lead.phone}
                          leadId={task.lead_id}
                          leadName={task.lead_name}
                          variant="icon"
                          size="sm"
                        />
                      )}

                      {/* Quick Actions */}
                      <button
                        onClick={() => setSelectedTask(task)}
                        className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
                        data-testid={`task-menu-${task.id}`}
                      >
                        ⋮
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Add Task Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowAddModal(false)}>
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-slate-200 dark:border-slate-700">
              <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">New Task</h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Task Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Follow up with John about proposal"
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-slate-900 dark:text-slate-100"
                  data-testid="task-title-input"
                  autoFocus
                />
              </div>

              {/* Type and Priority */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Type</label>
                  <select
                    value={formData.task_type}
                    onChange={(e) => setFormData({ ...formData, task_type: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-slate-900 dark:text-slate-100"
                  >
                    {Object.entries(TASK_TYPES).map(([key, config]) => (
                      <option key={key} value={key}>{config.icon} {config.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Priority</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-slate-900 dark:text-slate-100"
                  >
                    {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                      <option key={key} value={key}>{config.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Due Date and Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Due Date</label>
                  <input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-slate-900 dark:text-slate-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Time</label>
                  <input
                    type="time"
                    value={formData.due_time}
                    onChange={(e) => setFormData({ ...formData, due_time: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-slate-900 dark:text-slate-100"
                  />
                </div>
              </div>

              {/* Lead */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Related Lead</label>
                <select
                  value={formData.lead_id}
                  onChange={(e) => setFormData({ ...formData, lead_id: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-slate-900 dark:text-slate-100"
                >
                  <option value="">-- No Lead --</option>
                  {leads.map(lead => (
                    <option key={lead.id} value={lead.id}>{lead.name} {lead.organization ? `(${lead.organization})` : ''}</option>
                  ))}
                </select>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Notes</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Add any additional details..."
                  rows={3}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-slate-900 dark:text-slate-100"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary px-6 py-2 rounded-lg font-medium"
                  data-testid="submit-task-btn"
                >
                  Create Task
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Task Detail Modal */}
      {selectedTask && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedTask(null)}>
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-2xl">{TASK_TYPES[selectedTask.task_type]?.icon || '📌'}</span>
                <div>
                  <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">{selectedTask.title}</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{formatDueDate(selectedTask.due_date)}</p>
                </div>
              </div>

              {selectedTask.description && (
                <p className="text-slate-600 dark:text-slate-300 mb-4">{selectedTask.description}</p>
              )}

              {selectedTask.lead_name && (
                <Link
                  to={`/leads/${selectedTask.lead_id}`}
                  className="flex items-center gap-2 p-3 bg-slate-50 dark:bg-slate-700 rounded-lg mb-4 hover:bg-slate-100 dark:hover:bg-slate-600"
                >
                  <span className="text-lg">👤</span>
                  <span className="font-medium text-slate-900 dark:text-slate-100">{selectedTask.lead_name}</span>
                </Link>
              )}

              <div className="flex gap-2">
                <button
                  onClick={() => {
                    toggleComplete(selectedTask);
                    setSelectedTask(null);
                  }}
                  className={`flex-1 py-2 rounded-lg font-medium ${
                    selectedTask.status === 'done'
                      ? 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                      : 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400'
                  }`}
                >
                  {selectedTask.status === 'done' ? 'Reopen' : 'Mark Complete'}
                </button>
                <button
                  onClick={() => deleteTask(selectedTask.id)}
                  className="px-4 py-2 bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400 rounded-lg font-medium hover:bg-red-200 dark:hover:bg-red-900/70"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TaskCenter;
